// ExamScoresTable.tsx - Composant réutilisable pour afficher le tableau des notes d'examens
import React from 'react';
import { formatShortDateInTz, getAppTimezone } from '../utils/dateTime';

interface ExamScoresTableProps {
  testScores: any[];
  students: any[];
  subjects: any[];
  windowWidth: number;
  isRTL?: boolean;
  showActions: boolean;
  onDeleteTestScore?: (id: number) => void;
  formatDate?: (date: string) => string;
  t?: (key: string) => string;
}

const ExamScoresTable: React.FC<ExamScoresTableProps> = ({
  testScores,
  students,
  subjects,
  windowWidth,
  isRTL = true,
  showActions,
  onDeleteTestScore,
  formatDate = (date: string) => formatShortDateInTz(new Date(date), getAppTimezone(), 'fr-FR'),
  t = (key: string) => key,
}) => {
  const tr = (key: string, fallback: string) => {
    const result = t(key);
    return result === key ? fallback : result;
  };
  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      border: '1px solid #f0f0f0',
      overflow: 'hidden'
    }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          minWidth: '800px'
        }}>
          <thead style={{ backgroundColor: '#f5f5f5' }}>
            <tr>
              <th style={{
                padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px',
                textAlign: isRTL ? 'right' : 'left',
                fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px',
                fontWeight: '600',
                color: '#757575',
                width: windowWidth < 768 ? '30%' : '25%'
              }}>
                {tr('student', 'الطالب')}
              </th>
              <th style={{
                padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px',
                textAlign: isRTL ? 'right' : 'left',
                fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px',
                fontWeight: '600',
                color: '#757575',
                width: windowWidth < 768 ? '25%' : '20%',
                display: windowWidth < 768 ? 'none' : 'table-cell'
              }}>
                {tr('subject', 'المادة')}
              </th>
              <th style={{
                padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px',
                textAlign: 'center',
                fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px',
                fontWeight: '600',
                color: '#757575',
                width: windowWidth < 768 ? '25%' : '15%'
              }}>
                {tr('gradeOutOf20', 'الدرجة')}
              </th>
              <th style={{
                padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px',
                textAlign: 'center',
                fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px',
                fontWeight: '600',
                color: '#757575',
                width: windowWidth < 768 ? '20%' : '20%',
                display: windowWidth < 768 ? 'none' : 'table-cell'
              }}>
                {tr('date', 'التاريخ')}
              </th>
              {showActions && (
                <th style={{
                  padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px',
                  textAlign: 'center',
                  fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px',
                  fontWeight: '600',
                  color: '#757575',
                  width: windowWidth < 768 ? '25%' : '20%'
                }}>
                  {tr('actions', 'الإجراءات')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {testScores.length === 0 ? (
              <tr>
                <td colSpan={showActions ? 5 : 4} style={{
                  padding: windowWidth < 768 ? '30px 16px' : '48px',
                  textAlign: 'center',
                  color: '#9e9e9e'
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: windowWidth < 768 ? '8px' : '12px'
                  }}>
                    <div style={{ fontSize: windowWidth < 768 ? '36px' : '48px' }}>📊</div>
                    <div style={{ fontSize: windowWidth < 768 ? '14px' : '16px' }}>{tr('noTestScores', 'لا توجد نقط اختبار مسجلة')}</div>
                    {showActions && (
                      <div style={{ fontSize: windowWidth < 768 ? '12px' : '14px', color: '#6b7280' }}>
                        {tr('addTestScoreHint', 'أضف نقطة اختبار جديدة')}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              testScores.map((grade) => {
                const student = students.find(s => s.id === grade.student_id);
                const subject = subjects.find(s => s.id === grade.subject_id);
                
                return (
                  <tr key={grade.id} style={{
                    borderBottom: '1px solid #f0f0f0',
                    transition: 'background-color 0.2s'
                  }}>
                    <td style={{ padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px' }}>
                      <div style={{
                        fontSize: windowWidth < 768 ? '12px' : windowWidth < 1024 ? '13px' : '14px',
                        fontWeight: '500',
                        color: '#424242'
                      }}>
                        {student?.student_name || grade.student_name || tr('unknown', 'غير معروف')}
                      </div>
                    </td>
                    <td style={{ padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px', display: windowWidth < 768 ? 'none' : 'table-cell' }}>
                      <span style={{
                        padding: windowWidth < 768 ? '3px 6px' : '4px 8px',
                        fontSize: windowWidth < 768 ? '10px' : '12px',
                        fontWeight: '600',
                        borderRadius: '12px',
                        backgroundColor: '#f3e5f5',
                        color: '#7b1fa2'
                      }}>
                        {subject?.name || grade.subject_name || tr('unknown', 'غير معروف')}
                      </span>
                    </td>
                    <td style={{ padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px', textAlign: 'center' }}>
                      <span style={{
                        fontSize: windowWidth < 768 ? '14px' : windowWidth < 1024 ? '15px' : '16px',
                        fontWeight: 'bold',
                        color: grade.grade >= 15 ? '#4caf50' : grade.grade >= 12 ? '#ff9800' : '#f44336'
                      }}>
                        {grade.grade}/20
                      </span>
                    </td>
                    <td style={{ padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px', textAlign: 'center', display: windowWidth < 768 ? 'none' : 'table-cell' }}>
                      <div style={{
                        fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '13px',
                        color: '#757575',
                        fontWeight: '500'
                      }}>
                        {formatDate(grade.date)}
                      </div>
                    </td>
                    {showActions && (
                      <td style={{ padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: windowWidth < 768 ? '4px' : '8px', justifyContent: 'center' }}>
                          <button
                            onClick={() => onDeleteTestScore && onDeleteTestScore(grade.id)}
                            style={{
                              backgroundColor: 'rgba(211, 47, 47, 0.1)',
                              color: '#d32f2f',
                              padding: windowWidth < 768 ? '6px 10px' : '8px 12px',
                              fontSize: windowWidth < 768 ? '11px' : '13px',
                              border: '1px solid rgba(211, 47, 47, 0.2)',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(211, 47, 47, 0.2)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(211, 47, 47, 0.1)';
                            }}
                          >
                            <svg width={windowWidth < 768 ? 14 : 16} height={windowWidth < 768 ? 14 : 16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" />
                            </svg>
                            <span>{windowWidth < 768 ? '' : tr('delete', 'حذف')}</span>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExamScoresTable;
